import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { RootStackParamList } from '@/navigation/RootNavigator';
import {
  useGetThreadQuery,
  useCreateReplyMutation,
  useToggleSaveThreadMutation,
  useDeleteThreadMutation,
  useDeleteReplyMutation,
  useReportContentMutation,
  useRecordThreadViewMutation,
} from '@/store/api/corkBoard/corkBoardApi';
import { useAccumulatedPaging } from '@/hooks/common/useAccumulatedPaging';
import { useCorkBoardMutation } from './useCorkBoardMutation';
import { useToast } from '@/contexts/admin/ToastContext';
import { isFetchBaseQueryError } from '@/utils/admin/errorHandling';
import { showAlert } from '@/utils/common/alert';
import { CORK_BOARD_PAGINATION } from '@/constants/residents/corkBoardConstants';
import type { ReportReason, CorkBoardReply, CreateReplyRequest, DeleteReplyRequest, ReportContentRequest } from '@/types/residents/corkBoard';

type ThreadDetailNavigationProp = StackNavigationProp<RootStackParamList, 'ThreadDetail'>;

export const useThreadDetail = (threadId: string) => {
  const navigation = useNavigation<ThreadDetailNavigationProp>();
  const { showSuccess, showError } = useToast();

  // ── Reply pagination ──────────────────────────────────────────────────────
  const [replyPage, setReplyPage] = useState<number>(CORK_BOARD_PAGINATION.defaultPage);
  const [accumulationNonce, setAccumulationNonce] = useState(0);

  // ── Modal visibility ──────────────────────────────────────────────────────
  const [isActionsMenuVisible, setIsActionsMenuVisible] = useState(false);
  const [isReportModalVisible, setIsReportModalVisible] = useState(false);
  const [isDeleteConfirmVisible, setIsDeleteConfirmVisible] = useState(false);
  const [isDeleteReplyConfirmVisible, setIsDeleteReplyConfirmVisible] = useState(false);
  const [replyToDelete, setReplyToDelete] = useState<string | null>(null);
  const [hasReported, setHasReported] = useState(false);
  const [reportTarget, setReportTarget] = useState<{ threadId?: number; replyId?: number }>({});

  // ── API queries & mutations ───────────────────────────────────────────────
  const {
    data: threadData,
    isLoading,
    isFetching,
    isError,
    refetch,
  } = useGetThreadQuery({
    threadId,
    page: replyPage,
    pageSize: CORK_BOARD_PAGINATION.defaultPageSize,
  });

  const [createReplyTrigger, { isLoading: isSubmittingReply }] = useCreateReplyMutation();
  const [toggleSaveTrigger] = useToggleSaveThreadMutation();
  const [deleteThreadTrigger, { isLoading: isDeletingThread }] = useDeleteThreadMutation();
  const [deleteReplyTrigger, { isLoading: isDeletingReply }] = useDeleteReplyMutation();
  const [reportContentTrigger, { isLoading: isReporting }] = useReportContentMutation();
  const [recordView] = useRecordThreadViewMutation();

  const viewRecorded = useRef(false);
  useEffect(() => {
    if (viewRecorded.current) return;
    viewRecorded.current = true;
    recordView(threadId);
  }, [threadId, recordView]);

  const { execute: executeCreateReply } = useCorkBoardMutation<CreateReplyRequest, { id: string } | null>(
    createReplyTrigger, { errorFallback: 'Failed to post reply. Please try again.' },
  );
  const { execute: executeToggleSave } = useCorkBoardMutation<string, any>(
    toggleSaveTrigger, { errorFallback: 'Failed to update bookmark.' },
  );
  const { execute: executeDeleteThread } = useCorkBoardMutation<string, void>(
    deleteThreadTrigger, { errorFallback: 'Failed to delete thread.' },
  );
  const { execute: executeDeleteReply } = useCorkBoardMutation<DeleteReplyRequest, void>(
    deleteReplyTrigger, { errorFallback: 'Failed to delete reply.' },
  );
  const { execute: executeReport } = useCorkBoardMutation<ReportContentRequest, { id: string } | null>(
    reportContentTrigger, { errorFallback: 'Failed to submit report.' },
  );

  // ── Derived data ──────────────────────────────────────────────────────────
  const thread = threadData?.thread;
  const replies = threadData?.replies ?? [];
  const pagination = threadData?.pagination;
  const hasMoreReplies = pagination ? pagination.currentPage < pagination.totalPages - 1 : false;

  const accumulationKey = useMemo(
    () => `thread_${threadId}_${accumulationNonce}`,
    [threadId, accumulationNonce],
  );
  const { accumulatedRows: accumulatedReplies } = useAccumulatedPaging<CorkBoardReply>({
    rows: replies,
    accumulationKey,
  });

  const isInitialLoad = isLoading && accumulatedReplies.length === 0;

  const reportContentType: 'thread' | 'reply' = reportTarget.replyId ? 'reply' : 'thread';

  // ── Helpers ────────────────────────────────────────────────────────────────

  const resetReplies = useCallback(() => {
    setReplyPage(CORK_BOARD_PAGINATION.defaultPage);
    setAccumulationNonce((n) => n + 1);
  }, []);

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleGoBack = useCallback(() => {
    navigation.goBack();
  }, [navigation]);

  const handleLoadMoreReplies = useCallback(() => {
    if (!isFetching && hasMoreReplies) {
      setReplyPage((prev) => prev + 1);
    }
  }, [isFetching, hasMoreReplies]);

  const handleSubmitReply = useCallback(async (body: string): Promise<boolean> => {
    if (!body.trim()) return false;
    const result = await executeCreateReply(
      { threadId, body },
      { silent: false, onSuccess: () => resetReplies() },
    );
    return result.success;
  }, [threadId, executeCreateReply, resetReplies]);

  const handleToggleSave = useCallback(async () => {
    const result = await executeToggleSave(threadId, { silent: true });
    if (result.success) {
      const saved = result.data?.saved ?? !(thread?.isSaved);
      showSuccess(saved ? 'Thread saved' : 'Thread unsaved');
    } else {
      showError('Failed to save thread');
    }
  }, [threadId, executeToggleSave, thread?.isSaved, showSuccess, showError]);

  const handleDeleteThread = useCallback(async () => {
    setIsDeleteConfirmVisible(false);
    await executeDeleteThread(threadId, {
      onSuccess: () => navigation.goBack(),
    });
  }, [threadId, executeDeleteThread, navigation]);

  const openDeleteReplyConfirm = useCallback((replyId: string) => {
    setReplyToDelete(replyId);
    setIsDeleteReplyConfirmVisible(true);
  }, []);
  const closeDeleteReplyConfirm = useCallback(() => {
    setIsDeleteReplyConfirmVisible(false);
    setReplyToDelete(null);
  }, []);

  const handleDeleteReply = useCallback(async () => {
    if (!replyToDelete) return;
    setIsDeleteReplyConfirmVisible(false);
    await executeDeleteReply(
      { replyId: replyToDelete, threadId },
      { onSuccess: () => resetReplies() },
    );
    setReplyToDelete(null);
  }, [replyToDelete, threadId, executeDeleteReply, resetReplies]);

  const handleReportSubmit = useCallback(async (reason: ReportReason, details?: string) => {
    if (isReporting) return;
    await executeReport(
      { ...reportTarget, reason, details },
      {
        silent: true,
        onSuccess: () => {
          setIsReportModalVisible(false);
          setHasReported(true);
          showAlert('Report Submitted', 'Thank you. Our moderation team will review your report.');
        },
        onError: (_message, originalError) => {
          const isAlreadyReported =
            isFetchBaseQueryError(originalError) && originalError.status === 409;
          if (isAlreadyReported) {
            setIsReportModalVisible(false);
            setHasReported(true);
            showAlert('Already Reported', 'You have already reported this content. Our team is reviewing it.');
          } else {
            showAlert('Error', 'Failed to submit report. Please try again.');
          }
        },
      },
    );
  }, [executeReport, reportTarget, isReporting]);

  const openActionsMenu = useCallback(() => setIsActionsMenuVisible(true), []);
  const closeActionsMenu = useCallback(() => setIsActionsMenuVisible(false), []);

  const openReportModal = useCallback((target?: { threadId?: number; replyId?: number }) => {
    setReportTarget(target ?? { threadId: Number(threadId) });
    const menuWasOpen = isActionsMenuVisible;
    setIsActionsMenuVisible(false);
    setHasReported(false);
    if (menuWasOpen) {
      // Delay to let the actions menu Modal fully unmount before presenting
      // the report Modal (avoids iOS modal stacking dismissal issue)
      setTimeout(() => setIsReportModalVisible(true), 300);
    } else {
      setIsReportModalVisible(true);
    }
  }, [threadId, isActionsMenuVisible]);

  const closeReportModal = useCallback(() => setIsReportModalVisible(false), []);

  const openDeleteConfirm = useCallback(() => {
    const menuWasOpen = isActionsMenuVisible;
    setIsActionsMenuVisible(false);
    if (menuWasOpen) {
      setTimeout(() => setIsDeleteConfirmVisible(true), 300);
    } else {
      setIsDeleteConfirmVisible(true);
    }
  }, [isActionsMenuVisible]);
  const closeDeleteConfirm = useCallback(() => setIsDeleteConfirmVisible(false), []);

  const handleEditThread = useCallback(() => {
    setIsActionsMenuVisible(false);
    navigation.navigate('EditThread', { threadId });
  }, [threadId, navigation]);

  return {
    thread,
    accumulatedReplies,
    pagination,
    hasMoreReplies,
    isInitialLoad,
    isLoading,
    isFetching,
    isError,
    isSubmittingReply,
    isDeletingThread,
    isDeletingReply,
    isReporting,

    isActionsMenuVisible,
    isReportModalVisible,
    isDeleteConfirmVisible,
    isDeleteReplyConfirmVisible,
    hasReported,
    reportContentType,

    refetch,
    handleGoBack,
    handleLoadMoreReplies,
    handleSubmitReply,
    handleToggleSave,
    handleDeleteThread,
    handleDeleteReply,
    handleReportSubmit,
    handleEditThread,

    openActionsMenu,
    closeActionsMenu,
    openReportModal,
    closeReportModal,
    openDeleteConfirm,
    closeDeleteConfirm,
    openDeleteReplyConfirm,
    closeDeleteReplyConfirm,
  } as const;
};
